/**
 * Parse a boolean attribute value
 *
 * @param value - String value to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed boolean
 */
export const parseBoolean = (
  value: string | null | undefined,
  defaultValue: boolean = false,
): boolean => {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  const trimmed = value.trim().toLowerCase();

  if (trimmed === 'true' || trimmed === '1' || trimmed === 'yes') {
    return true;
  }

  if (trimmed === 'false' || trimmed === '0' || trimmed === 'no') {
    return false;
  }

  return defaultValue;
};

/**
 * Parse an integer attribute value
 *
 * @param value - String value to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed integer
 */
export const parseInteger = (
  value: string | null | undefined,
  defaultValue: number = 0,
): number => {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  const trimmed = value.trim();

  // Reject percentage values - return default
  if (trimmed.includes('%')) {
    return defaultValue;
  }

  const cleaned = trimmed.replace(/px|em|rem/gi, '');
  const parsed = parseInt(cleaned, 10);

  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Parse a float attribute value
 *
 * @param value - String value to parse
 * @param defaultValue - Default value if parsing fails
 * @returns Parsed float
 */
export const parseFloat_ = (value: string | null | undefined, defaultValue: number = 0): number => {
  if (value === null || value === undefined) {
    return defaultValue;
  }

  const trimmed = value.trim();
  const parsed = parseFloat(trimmed);

  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Parse a value with optional unit suffix (px or %)
 *
 * Handles formats:
 * - "500px" → { value: 500, unit: 'px' }
 * - "80%" → { value: 80, unit: '%' }
 * - "500" (no unit) → { value: 500, unit: 'px' } (defaults to px)
 *
 * @param value - The string value to parse
 * @param defaultValue - Default numeric value if parsing fails
 * @param defaultUnit - Default unit if not specified
 * @returns Object with parsed value and unit
 */
export const parseValueWithUnit = (
  value: string | null,
  defaultValue: number,
  defaultUnit: 'px' | '%',
): { value: number; unit: 'px' | '%' } => {
  if (!value) {
    return { value: defaultValue, unit: defaultUnit };
  }

  const trimmed = value.trim();

  if (trimmed.endsWith('%')) {
    const num = parseFloat(trimmed.slice(0, -1));
    return { value: isNaN(num) ? defaultValue : num, unit: '%' };
  }

  // Strip 'px' suffix if present, then parse as integer
  const numStr = trimmed.endsWith('px') ? trimmed.slice(0, -2) : trimmed;
  const num = parseInt(numStr, 10);
  return { value: isNaN(num) ? defaultValue : num, unit: 'px' };
};
